<template>
    <div class="h-full">
        <div class="max-w-8xl mx-auto px-6 rounded shadow">
            <div class="flex flex-col md:flex-row">
                <!-- name -->
                <div class="mb-4 flex flex-col w-full md:w-[40rem]">
                    <label for="name" class="block text-sm font-medium">Space Advertiser name</label>
                    <div>
                        <InputText :disabled="isAddingDeal" class="w-full md:w-3/5 mt-1" id="name" v-model="form.name"
                            :invalid="nameError !== null" placeholder="Name" required />
                        <small class="error block" v-if="nameError">{{ nameError }}</small>
                    </div>
                </div>
                <!-- logo selector -->
                <div class="mb-4 flex flex-col w-full md:w-[40rem]">
                    <label for="logo" class="block text-sm font-medium">Logo</label>
                    <div>
                        <div class="flex gap-2">
                            <Select id="logoUrl" :disabled="isAddingDeal" v-model="form.logoUrl" :options="logos" optionLabel="name"
                                optionValue="url" placeholder="Select a logo" class="w-full md:w-3/5 mt-1" filter
                                :virtualScrollerOptions="{ itemSize: 50 }">
                                <template #option="slotProps">
                                    <div class="flex align-items-center gap-2">
                                        <img :src="slotProps.option.url" :alt="slotProps.option.name"
                                            style="width: 20px" />
                                        <div>{{ slotProps.option.name }}</div>
                                    </div>
                                </template>
                            </Select>
                            <Button icon="pi pi-refresh" :loading="isRefreshingLogos" rounded text size="small"
                                v-tooltip.bottom="isRefreshingLogos ? 'Refreshing logos...' : 'Refresh logos'"
                                @click="refreshLogos" severity="secondary" />
                        </div>
                    </div>
                </div>
                <!-- selected logo preview -->
                <div class="mb-4 flex flex-col w-full md:w-[40rem]">
                    <label class=" text-sm font-medium ">Selected Logo</label>
                    <div class="mt-1">
                        <img v-if="form.logoUrl" :src="form.logoUrl" :alt="form.name"
                            class="w-48 h-32 object-contain bg-white rounded-md p-2" />
                        <div v-else class="w-32 h-32 border rounded p-2 flex items-center justify-center text-gray-400">
                            No logo selected
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!showAddMarketSection" class="mb-2 p-1">
                <div class="flex flex-col lg:flex-row gap-x-8 h-full">
                    <div class="lg:w-12/12 w-full h-full overf`low`-auto  p-2 ">
                        <div class="flex justify-between">
                            <h2 class="text-xl font-bold mb-4 border-b-2 border-black">Markets</h2>
                            <MultiSelect :disabled="showAddMarketSection || isAddingDeal" v-model="selectedMarkets" filter
                                :options="marketOptions" placeholder="Add Market/s" class="sm:w-2/5 pt-1"
                                :virtualScrollerOptions="{ itemSize: 30 }" :maxSelectedLabels="5"
                                selectedItemsLabel="{0} markets added">
                                <template #option="slotProps">
                                    <div class="flex items-center">
                                        <span>{{ slotProps.option }}</span>
                                        <span :class="marketClass(slotProps.option)" class="ml-2"></span>
                                    </div>
                                </template>
                            </MultiSelect>
                            <Button :disabled="showAddMarketSection || isAddingDeal" icon="pi pi-plus " v-tooltip.bottom="'Add Market'"
                                rounded text @click="addMarket" severity="secondary" outlined />
                        </div>
                        <div class="grid  md:grid-cols-1 gap-1 max-h-[35rem] overflow-y-auto mt-2">
                            <Card v-for="({ deals, marketName }) in advertiserMarkets" :key="marketName"
                                class="flex flex-col mt-2 border gap-2 p-2  shadow-md w-full card-bg">
                                <template #content>
                                    <div class="flex w-full justify-between">
                                        <!-- Market name on its own line with left alignment -->
                                        <div class="font-semibold">Market: {{ marketName }}
                                            <span :class="marketClass(marketName)" class="ml-2"></span>
                                        </div>
                                        <Button icon="pi pi-plus" :disabled="isAddingDeal" v-tooltip.bottom="'Add new deal'" rounded text
                                            severity="secondary" @click="addNewDeal(marketName)" outlined />
                                    </div>

                                    <!-- Loop through each deal for the current market -->
                                    <div v-for="(deal, index) in deals" :key="index"
                                        :id="deal.dealId ? 'deal-card-' + deal.dealId : undefined"
                                        :ref="el => { if (isLastAddedDeal(marketName, index)) lastDealRef = el }"
                                        :class="[
                                            'flex justify-between items-end border rounded mt-3 p-2',
                                            { 'modified-deal': isModifiedDeal(marketName, index) },
                                            { 'highlight-border': isLastAddedDeal(marketName, index) },
                                            { 'focused-deal': focusedDealId && deal.dealId === focusedDealId }
                                        ]"
                                        @click="() => watchDealChanges(marketName, index, deal)">
                                        <div
                                            :class="`grid  md:grid-cols-6 lg:grid-cols-10 xl:grid-cols-12 gap-x-6 items-center`">
                                            <div class="flex flex-col col-span-2 lg:col-span-2">
                                                <label :for="'deal-type-' + marketName + '-' + index"
                                                    class="block text-sm font-medium">Deal Type</label>
                                                <div>
                                                    <Select :id="'deal-type-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        filter
                                                        class="w-full mt-1"
                                                        option-label="name"
                                                        option-value="code"
                                                        v-model="deal.dealType"
                                                        :options="dealTypes"
                                                        aria-labelledby="basic"
                                                        placeholder="Select a deal type">
                                                    </Select>
                                                    <br />
                                                    <small class="error" v-if="errors[marketName][index]?.dealType">{{
                                                        errors[marketName][index].dealType }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-2">
                                                <label :for="'vertical-' + marketName + '-' + index"
                                                    class="block text-sm font-medium">Vertical
                                                </label>
                                                <div>
                                                    <Select :id="'vertical-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        filter
                                                        class="w-full mt-1"
                                                        option-label="name"
                                                        option-value="code"
                                                        v-model="deal.vertical"
                                                        :options="verticals"
                                                        aria-labelledby="basic">
                                                    </Select>
                                                    <br />
                                                    <small class="error" v-if="errors[marketName][index]?.vertical">{{
                                                        errors[marketName][index].vertical }}</small>
                                                </div>
                                            </div>
                                            <div
                                                class="flex flex-col col-span-2 lg:col-span-2 bg-blue-100 p-2 rounded-xl">
                                                <div class="text-xs text-blue-800 font-medium -mt-1 mb-1">Customer Deal
                                                </div>
                                                <label :for="'customer-commission-type-' + marketName + '-' + index"
                                                    class="block text-xs font-medium text-gray-700">
                                                    <span
                                                        v-if="deal.customerDeal.commissionType && deal.ourDeal.commissionType"
                                                        class="px-1.5 py-0.5 text-xs rounded-full"
                                                        :class="getMarginClass(deal.customerDeal, deal.ourDeal)">
                                                        {{ getMarginDisplay(deal.customerDeal, deal.ourDeal) }}
                                                    </span>
                                                </label>
                                                <div>
                                                    <Select
                                                        :id="'customer-select-commission-type-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        filter
                                                        class="w-full mt-1"
                                                        option-label="name"
                                                        option-value="code"
                                                        v-model="deal.customerDeal.commissionType"
                                                        :options="commissionTypes"
                                                        @change="(event) => handleCustomerCommissionTypeChange(event, marketName, index)"
                                                        aria-labelledby="basic">
                                                    </Select>
                                                    <br />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.customerDeal.commissionType">{{
                                                            errors[marketName][index].customerDeal.commissionType }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-1 bg-blue-100 p-2 rounded-xl"
                                                :class="{ 'md:-ml-8': true }"
                                                v-if="deal.customerDeal.commissionType.includes('CPA')">
                                                <div class="invisible text-xs text-blue-800 font-medium -mt-1 mb-1">C
                                                </div>
                                                <label :for="'customer-cpa' + marketName + '-' + index"
                                                    class="block text-xs font-medium text-gray-700">CPA</label>
                                                <div>
                                                    <InputNumber id="'customer-cpa' + marketName + '-' + index"
                                                        v-model="deal.customerDeal.cpa"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        placeholder="CPA"
                                                        aria-labelledby="basic"
                                                        class="mt-1"
                                                        required
                                                        fluid />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.customerDeal.cpa">{{
                                                            errors[marketName][index].customerDeal.cpa }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-1 bg-blue-100 p-2 md:-ml-8 rounded-xl"
                                                v-if="deal.customerDeal.commissionType.includes('REV')">
                                                <div class="invisible text-xs ">C</div>
                                                <label :for="'rev-' + marketName + '-' + index"
                                                    class="block text-xs font-medium text-gray-700">Revshare</label>
                                                <div>
                                                    <InputNumber suffix="%"
                                                        id="'customer-rev-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        v-model="deal.customerDeal.rev"
                                                        placeholder="REV"
                                                        aria-labelledby="basic"
                                                        class="mt-1"
                                                        required
                                                        fluid />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.customerDeal.rev">{{
                                                            errors[marketName][index].customerDeal.rev }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-1 bg-blue-100 p-2 rounded-xl"
                                                :class="{ 'md:-ml-8': true }"
                                                v-if="deal.customerDeal.commissionType.includes('CPL')">
                                                <div class="invisible text-xs text-blue-800 font-medium -mt-1 mb-1">C
                                                </div>
                                                <label :for="'cpl-' + marketName + '-' + index"
                                                    class="block text-xs font-medium text-gray-700">CPL</label>
                                                <div>
                                                    <InputNumber id="'customer-cpl-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        v-model="deal.customerDeal.cpl"
                                                        placeholder="CPL"
                                                        aria-labelledby="basic"
                                                        class="mt-1"
                                                        required
                                                        fluid />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.customerDeal.cpl">{{
                                                            errors[marketName][index].customerDeal.cpl }}</small>
                                                </div>
                                            </div>
                                            <div
                                                class="flex flex-col col-span-2 lg:col-span-2 bg-red-100 p-2 rounded-xl">
                                                <div class="text-xs text-blue-800 font-medium -mt-1 mb-1">Our Deal
                                                </div>
                                                <label :for="'our-commission-type-' + marketName + '-' + index"
                                                    class="block text-xs font-medium text-gray-700">Commission
                                                    Type</label>
                                                <div>
                                                    <Select
                                                        :id="'our-select-commission-type-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        filter
                                                        class="w-full mt-1"
                                                        option-label="name"
                                                        option-value="code"
                                                        v-model="deal.ourDeal.commissionType"
                                                        :options="commissionTypes"
                                                        aria-labelledby="basic">
                                                    </Select>
                                                    <br />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.ourDeal.commissionType">{{
                                                            errors[marketName][index].ourDeal.commissionType }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-1 bg-red-100 p-2 rounded-xl"
                                                :class="{ 'md:-ml-8': true }"
                                                v-if="deal.ourDeal.commissionType.includes('CPA')">
                                                <div class="invisible text-xs text-blue-800 font-medium -mt-1 mb-1">C
                                                </div>
                                                <label :for="'our-cpa-' + marketName + '-' + index"
                                                    class="block text-xs font-medium text-gray-700">CPA</label>
                                                <div>
                                                    <InputNumber id="'our-cpa-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        v-model="deal.ourDeal.cpa"
                                                        placeholder="CPA"
                                                        aria-labelledby="basic"
                                                        class="mt-1"
                                                        required
                                                        fluid />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.ourDeal.cpa">{{
                                                            errors[marketName][index].ourDeal.cpa }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-1 bg-red-100 p-2 rounded-xl"
                                                :class="{ 'md:-ml-8': true }"
                                                v-if="deal.ourDeal.commissionType.includes('CPL')">
                                                <div class="invisible text-xs text-blue-800 font-medium -mt-1 mb-1">C
                                                </div>
                                                <label :for="'our-cpl-' + marketName + '-' + index"
                                                    class="block text-xs font-medium text-gray-700">CPL</label>
                                                <div>
                                                    <InputNumber id="'our-cpl-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        v-model="deal.ourDeal.cpl"
                                                        placeholder="CPL"
                                                        aria-labelledby="basic"
                                                        class="mt-1"
                                                        required
                                                        fluid />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.ourDeal.cpl">{{
                                                            errors[marketName][index].ourDeal.cpl }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-1 bg-red-100 p-2 md:-ml-8 rounded-xl"
                                                v-if="deal.ourDeal.commissionType.includes('REV')">
                                                <div class="invisible text-xs ">C</div>
                                                <label :for="'our-rev-' + marketName + '-' + index"
                                                    class="block text-xs font-medium text-gray-700">Revshare</label>
                                                <div>
                                                    <InputNumber suffix="%"
                                                        id="'our-rev-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        v-model="deal.ourDeal.rev"
                                                        placeholder="REV"
                                                        aria-labelledby="basic"
                                                        class="mt-1"
                                                        required
                                                        fluid />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.ourDeal.rev">{{
                                                            errors[marketName][index].ourDeal.rev }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-2">
                                                <label :for="'voonixAdvertiser-' + marketName + '-' + index"
                                                    class="block text-sm font-medium">Voonix
                                                    Advertiser</label>
                                                <div>
                                                    <Select :id="'voonixAdvertiser-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        filter
                                                        class="w-full mt-1"
                                                        option-label="voonixAdvertiserName"
                                                        option-value="voonixAdvertiserId"
                                                        v-model="deal.voonixAdvertiserId"
                                                        :options="voonixAdvertisers"
                                                        aria-labelledby="basic"
                                                        placeholder="Select one">
                                                    </Select>
                                                    <br />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.voonixAdvertiserId">{{
                                                            errors[marketName][index].voonixAdvertiserId }}</small>
                                                </div>
                                            </div>
                                            <div class="flex flex-col col-span-2 lg:col-span-2" v-if="!deal.isPublic">
                                                <label :for="'selected-users-' + marketName + '-' + index"
                                                    class="block text-sm font-medium">Users</label>
                                                <div>
                                                    <MultiSelect :id="'selected-users-' + marketName + '-' + index"
                                                        :disabled="isDealDisabled(marketName, index)"
                                                        filter
                                                        class="w-full mt-1"
                                                        option-label="name"
                                                        option-value="id"
                                                        v-model="deal.selectedUsers"
                                                        :options="allUsers"
                                                        :maxSelectedLabels="2"
                                                        :selectedItemsLabel="getSelectedLabel(deal.selectedUsers.length)"
                                                        aria-labelledby="basic"
                                                        placeholder="Select a user">
                                                    </MultiSelect>
                                                    <br />
                                                    <small class="error"
                                                        v-if="errors[marketName][index]?.selectedUsers">{{
                                                            errors[marketName][index].selectedUsers }}</small>
                                                </div>
                                            </div>
                                            <!-- Date-effective versioning: only when editing an existing (saved) deal.
                                                 Consumed by the backend ONLY if the deal's rates actually change. -->
                                            <template v-if="props.isEditing && !deal.temp && !isDealDisabled(marketName, index)">
                                                <div class="flex flex-col col-span-2 lg:col-span-2">
                                                    <label class="block text-sm font-medium">Change type</label>
                                                    <Select class="w-full mt-1" :options="changeTypes"
                                                        option-label="name" option-value="code"
                                                        v-model="deal._changeType"
                                                        v-tooltip.bottom="'Correction overrides the existing version retroactively; rate/commission change applies from the effective date forward.'"
                                                        placeholder="Select change type" />
                                                </div>
                                                <div class="flex flex-col col-span-2 lg:col-span-2"
                                                    v-if="deal._changeType !== 'correction'">
                                                    <label class="block text-sm font-medium">Effective from</label>
                                                    <DatePicker class="w-full mt-1" dateFormat="yy-mm-dd" showIcon
                                                        v-model="deal._effectiveFrom"
                                                        v-tooltip.bottom="'The day the new rates start applying. Earlier days keep the previous version.'" />
                                                </div>
                                                <div class="flex flex-col col-span-full">
                                                    <label class="block text-sm font-medium">Reason (optional)</label>
                                                    <InputText class="w-full mt-1" v-model="deal._changeReason"
                                                        placeholder="Why is this deal changing?" />
                                                </div>
                                            </template>
                                            <div class="col-span-full text-[11px] opacity-50 leading-none mt-1"
                                                :title="deal.createdAt ? new Date(deal.firstCreatedAt || deal.createdAt).toISOString() : ''">
                                                {{ dealDateLabel(deal) }}
                                            </div>
                                        </div>
                                        <div class="flex flex-col items-center self-stretch justify-between">
                                            <div class="flex flex-col">
                                                <label :for="'ispublic-' + marketName + '-' + index"
                                                    class="block text-sm font-medium ">Public</label>
                                                <ToggleSwitch :id="'ispublic-' + marketName + '-' + index"
                                                    :disabled="isDealDisabled(marketName, index)"
                                                    v-model="deal.isPublic" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label :for="'ispublic-' + marketName + '-' + index"
                                                    class="block text-sm font-medium ">Toplist</label>
                                                <ToggleSwitch v-tooltip.top="'Show deal toplist'"
                                                    :disabled="isDealDisabled(marketName, index)"
                                                    class="w-40"
                                                    v-model="deal.toplist" />
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </Card>

                        </div>
                    </div>

                </div>
                <div class="w-full flex justify-center content-center pb-2">
                <Button v-if="!isAddingDeal" class="md:w-1/5 " :loading="isSaving" severity="primary"
                    :label="isEditing ? 'Edit Space Advertiser' : 'Add Space Advertiser'" @click="handleSaveOrEdit" />
                <Button v-else class="md:w-1/5 " :loading="isSaving" severity="primary"
                    label="Add Deal" @click="handleAddDeal" />
            </div>
                <div class="lg:w-12/12 w-full h-full overf-auto pt-10">
                    <h2 class="text-xl font-bold mb-4 border-b-2 border-black">Mapped Voonix Advertisers</h2>
                    <DataTable v-if="voonixAdvertisers.length > 0" id="mappedAdvertisers" :value="voonixAdvertisers">
                        <Column field="voonixAdvertiserName" :headerStyle="headerStyles"></Column>
                    </DataTable>
                    <div v-else class="text-center text-xl mb-5">No mapped Voonix advertisers found</div>
                </div>
            </div>


        </div>
    </div>
</template>

<script setup>
import { ref, reactive, defineProps, computed, watch, onMounted, nextTick } from "vue";
import { useToast } from 'primevue/usetoast';
const toast = useToast();
import AdvertiserService from "@/services/AdvertiserService";
import UsersService from "@/services/UsersService";
import SlugService from "@/services/SlugService";
import { marketClass, getCountryCode, markets } from '@/utils/markets';
import { getSiteTypes } from '@/utils/siteTypes';
import { verticals, getVerticalName } from '@/utils/verticals';
import { headerStyles } from '@/global/headerStyles';
import { formatDateTime, formatDate } from '@/utils/dateHelper';

const logos = ref([]);
const marketOptions = ref(markets);
const allUsers = ref([]);
const props = defineProps({
    isEditing: Boolean,      // Boolean to check if it's in edit mode
    advertiserData: Object,    // The advertiser data to populate the form (if in edit mode)
    // Deep link: scroll to and briefly highlight this dealId once the form is
    // populated. A market can hold several deals with identical terms, so the
    // dealId is the only reliable way to point at the right one.
    focusDealId: { type: String, required: false, default: null },
    presetData: {
        type: Object,
        required: false,
        default: null
    }
});

const emit = defineEmits(['advertiserAdded', 'advertiserEdited', 'dealAdded']);
const isAddingDeal = computed(() => {
    return props.presetData !== null;
});
const isSaving = ref(false);
const defaultVertical = 'casino';

const defaultDealType = 'ppc';
const defaultIsPublic = false;
const dealTypes = ref(getSiteTypes());

const defaultCommissionType = 'CPA';
const commissionTypes = ref([
    { name: 'Not set', code: 'NOTSET' },
    { name: 'Revshare', code: 'REV' },
    { name: 'CPA', code: 'CPA' },
    // { name: 'CPL', code: 'CPL' },
    { name: 'Rev+CPA', code: 'REV-CPA' },
    // { name: 'Rev+CPL', code: 'REV-CPL' },
    // { name: 'CPA+CPL', code: 'CPA-CPL' }
]);

// Date-effective deal versioning — the intent of an edit to an existing deal.
// Two intents only: a forward-dated 'change' (new terms from a date, earlier days
// keep the old version) vs a retroactive 'correction' (the old value was never
// right, so it reuses that version's effectiveFrom and overrides its whole window).
// Whether a change moved the rate or the commission is already visible by diffing
// the versions, so it isn't a third option. See advertiser-service.updateAdvertiser
// + helpers/effective-deal.js.
const focusedDealId = ref(null);

/**
 * Scroll the requested deal into view and flash it, so a deep link from the
 * publisher view lands on the exact deal rather than the top of the modal.
 * Waits a tick for the dialog content to render before measuring.
 */
async function focusDeal(dealId) {
    if (!dealId) return;
    await nextTick();
    const el = document.getElementById('deal-card-' + dealId);
    if (!el) return;              // deal not on this advertiser (stale link) - no-op
    focusedDealId.value = dealId;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => { if (focusedDealId.value === dealId) focusedDealId.value = null; }, 4000);
}

const changeTypes = ref([
    { name: 'Deal change', code: 'change' },
    { name: 'Correction (fix a mistake)', code: 'correction' },
]);

const getColumnCount = (commissionType) => {
    return commissionType.includes('-') ? 6 : 5;
};

const getSelectedLabel = (count) => {
    return `${count} user${count !== 1 ? 's' : ''} selected`;
};
// Dynamic markets array to hold market-specific form data
const selectedMarkets = ref([]);

const advertiserMarkets = ref([]);

// Errors array for each market section
const errors = ref({});

const nameError = ref(null);

const form = reactive({
    name: '',
    logoUrl: ''
});

const voonixAdvertisers = ref(new Array(3));

// for auto scroll to last added deal
const lastDealRef = ref(null);
const lastAddedMarket = ref(null);
const lastAddedDealIndex = ref(null);

// Add this helper function
const isLastAddedDeal = (marketName, dealIndex) => {
    return marketName === lastAddedMarket.value && dealIndex === lastAddedDealIndex.value;
};

const hasInitializedPreset = ref(false);

// Add these refs for tracking changes
const modifiedDeals = ref(new Map()); // Initialize as an empty Map

// And add a computed property to safely access the Map
const isModifiedDeal = (marketName, index) => {
    return modifiedDeals.value?.has(`${marketName}-${index}`) || false;
};

// Function to track deal modifications
const trackDealModification = (marketName, dealIndex, deal) => {
    const key = `${marketName}-${dealIndex}`;
    modifiedDeals.value.set(key, {
        marketName,
        dealIndex,
        deal: JSON.parse(JSON.stringify(deal)) // Deep copy to track the state at modification
    });
};

//ui method
function addMarket(dealType = null, userId = null) {
    // Check if dealType is an event object and set to null
    if (dealType?.constructor?.name === 'PointerEvent') {
        dealType = null;
    }
    
    selectedMarkets.value.forEach((marketName) => {
        console.log(marketName);

        // Check if the market already exists in advertiserMarkets
        let existingMarket = advertiserMarkets.value.find(
            (market) => market.marketName === marketName
        );

        const toplist = advertiserMarkets.value.length === 0 ? true : false;

        if (!existingMarket) {
            // If the market doesn't exist, initialize it with an empty deals array
            existingMarket = {
                marketName,
                deals: []
            };
            advertiserMarkets.value.push(existingMarket);
        }

        // Add a new deal to the market's deals array
        existingMarket.deals.push({
            temp: true,
            dealType: dealType ? dealType : defaultDealType,
            vertical: defaultVertical,
            toplist: userId ? true : defaultIsPublic,
            isPublic: userId ? false : defaultIsPublic,
            selectedUsers: userId ? [userId] : [],
            customerDeal: {
                commissionType: defaultCommissionType,
                rev: null,
                cpa: null,
                cpl: null
            },
            ourDeal: {
                commissionType: defaultCommissionType,
                rev: null,
                cpa: null,
                cpl: null
            },
            voonixAdvertiserId: voonixAdvertisers.value.length > 0 
                ? voonixAdvertisers.value.find(adv => adv.isActive)?.voonixAdvertiserId : null
        });

        // Initialize errors for the new deal
        if (!errors.value[marketName]) {
            errors.value[marketName] = [];
        }
        errors.value[marketName].push({
            dealType: null,
            selectedUsers: null,
            vertical: null,
            customerDeal: {
                commissionType: null,
                rev: null,
                cpa: null,
                cpl: null
            },
            ourDeal: {
                commissionType: null,
                rev: null,
                cpa: null,
                cpl: null
            }
        });

        console.log('errors', errors.value);

        // Track the last added deal
        lastAddedMarket.value = marketName;
        lastAddedDealIndex.value = existingMarket.deals.length - 1;

        // Scroll to the new deal
        nextTick(() => {
            if (lastDealRef.value) {
                lastDealRef.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    });

    // Remove selected markets from the available options
    marketOptions.value = marketOptions.value.filter(
        (market) => !selectedMarkets.value.includes(market)
    );

    // Clear selected markets after adding
    selectedMarkets.value = null;
}

// ui method
/**
 * Small per-deal date shown at the bottom of each row.
 *
 * firstCreatedAt comes from the server (earliest version of this dealId) and is
 * the date the deal was added. Changing a deal's terms appends a new version, so
 * for v2+ the latest version's createdAt is when the current terms were set —
 * shown alongside so an edited deal isn't mistaken for a new one. Falls back to
 * createdAt if the server hasn't been updated yet.
 *
 * Unsaved deals carry temp:true and no date at all; formatDateTime throws on an
 * invalid date, so they get their own label instead.
 */
function dealDateLabel(deal) {
    if (!deal || deal.temp) return 'New — not saved yet';
    const first = deal.firstCreatedAt || deal.createdAt;
    if (!first || Number.isNaN(new Date(first).getTime())) return '';
    const added = `Added ${formatDateTime(first)}`;
    if ((deal.version || 1) > 1 && deal.createdAt && !Number.isNaN(new Date(deal.createdAt).getTime())) {
        return `${added} · v${deal.version} changed ${formatDateTime(deal.createdAt)}`;
    }
    return added;
}

function addNewDeal(marketName, dealType = defaultDealType, userId = null) {
    // Find the market object with the specified marketName
    const market = advertiserMarkets.value.find(m => m.marketName === marketName);
    
    if (market) {
        const newDeal = {
            temp: true,
            dealType: dealType,
            vertical: defaultVertical,
            isPublic: userId ? false : defaultIsPublic,
            toplist: userId ? true : defaultIsPublic,
            selectedUsers: userId ? [userId] : [],
            customerDeal: {
                commissionType: defaultCommissionType,
                rev: null,
                cpa: null,
                cpl: null
            },
            ourDeal: {
                commissionType: defaultCommissionType,
                rev: null,
                cpa: null,
                cpl: null
            },
            voonixAdvertiserId: (() => {
                const activeAdvertisers = voonixAdvertisers.value.filter(adv => adv.isActive);
                return activeAdvertisers.length === 1 ? activeAdvertisers[0].voonixAdvertiserId : null;
            })()
        };

        market.deals.push(newDeal);
        
        // Track the new deal
        trackDealModification(marketName, market.deals.length - 1, newDeal);
        
        // Initialize errors for the new deal
        if (!errors.value[marketName]) {
            errors.value[marketName] = [];
        }
        errors.value[marketName].push({
            dealType: null,
            selectedUsers: null,
            vertical: null,
            customerDeal: {
                commissionType: null,
                rev: null,
                cpa: null,
                cpl: null
            },
            ourDeal: {
                commissionType: null,
                rev: null,
                cpa: null,
                cpl: null
            },
            voonixAdvertiserId: null
        });

        console.log('Updated advertiserMarkets:', advertiserMarkets.value);
        console.log('Updated errors:', errors.value);

        // Track the last added deal
        lastAddedMarket.value = marketName;
        lastAddedDealIndex.value = market.deals.length - 1;

        // Scroll to the new deal
        nextTick(() => {
            if (lastDealRef.value) {
                lastDealRef.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    } else {
        console.warn(`Market with name "${marketName}" not found.`);
    }
}

// Modify the editAdvertiser function to log changes instead of making API call
async function editAdvertiser() {
    if (validateForm()) {
        console.log('Form is valid. Proceeding to edit:', form.name, advertiserMarkets.value);
        isSaving.value = true;  // Show a loading state during the save operation

        try {
            // Prepare the updated advertiser data. Translate the transient
            // date-effective inputs (_changeType/_effectiveFrom/_changeReason) into
            // the backend deal fields; strip the underscore-prefixed form state.
            const markets = advertiserMarkets.value.map(market => ({
                ...market,
                deals: (market.deals || []).map(deal => {
                    const { _changeType, _effectiveFrom, _changeReason, ...rest } = deal;
                    return {
                        ...rest,
                        changeType: _changeType || 'change',
                        // A bare local calendar day. Sending the Date itself serialises local
                        // midnight to a UTC instant, which is the previous day for UTC+ admins.
                        effectiveFrom: formatDate(_effectiveFrom || new Date()),
                        changeReason: _changeReason || undefined,
                    };
                }),
            }));
            const updatedAdvertiser = {
                name: form.name,
                logoUrl: form.logoUrl,
                markets
            };
            console.log('updated', updatedAdvertiser)
            
            // Log modified deals
            const modifications = Array.from(modifiedDeals.value.values());
            console.log('Modified Deals:', modifications);
            console.log('Total modified deals:', modifications.length);

            // Make the API call to update the advertiser using the ID
            const response = await AdvertiserService.update(form.id, updatedAdvertiser);

            // Handle successful update
            console.log('Advertiser edited successfully:', response.data);
            resetForm();
            emit('advertiserEdited');

        } catch (error) {
            console.error('Error editing advertiser:', error);
            toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to update advertiser', life: 3000 });
        } finally {
            isSaving.value = false;
        }
    } else {
        console.log('Form is invalid. Please correct the errors.', errors.value);
    }
}

// Add watchers for deal changes
const watchDealChanges = (marketName, dealIndex, deal) => {
    watch(() => [
        deal.dealType,
        deal.vertical,
        deal.isPublic,
        deal.toplist,
        deal.selectedUsers,
        deal.customerDeal,
        deal.ourDeal,
        deal.voonixAdvertiserId
    ], () => {
        trackDealModification(marketName, dealIndex, deal);
    }, { deep: true });
};

//ui unused method
function cloneMarket(currentMarket) {
    console.log(currentMarket);
    const currentIndex = advertiserMarkets.value.indexOf(currentMarket);

    // Insert the cloned market just after the current market
    if (currentIndex !== -1) {
        advertiserMarkets.value.splice(currentIndex + 1, 0, {
            name: currentMarket.name,
            dealType: currentMarket.dealType,
            deal: currentMarket.deal
        });

        // Insert a corresponding error object at the same position
        errors.value.splice(currentIndex + 1, 0, {
            name: null,
            dealType: null,
            deal: null
        });
    }
}

const validateForm = () => {
    let hasErrors = false; // Flag to track if there are any errors

    // Validate name field
    nameError.value = form.name.length >= 3 ? null : 'Name must be at least 3 characters long.';
    if (nameError.value !== null) {
        hasErrors = true; // If name has an error, set hasErrors to true
    }

    // Validate each market in advertiserMarkets
    advertiserMarkets.value.forEach((market) => {
        const marketName = market.marketName;
        const marketDeals = market.deals;

        // Initialize errors for this market if not already initialized
        if (!errors.value[marketName]) {
            errors.value[marketName] = [];
        }

        // Iterate over the deals within the market
        marketDeals.forEach((deal, index) => {
            // Ensure that errors for this specific deal are initialized
            if (!errors.value[marketName][index]) {
                errors.value[marketName][index] = {
                    customerDeal: {},
                    ourDeal: {}
                };
            }

            // Validate dealType and vertical
            errors.value[marketName][index].dealType = deal.dealType ? null : 'Please select a deal type.';
            errors.value[marketName][index].vertical = deal.vertical ? null : 'Please select a vertical.';

            // Validate customerDeal
            errors.value[marketName][index].customerDeal.commissionType = deal.customerDeal.commissionType ? null : 'Please select a commission type.';
            errors.value[marketName][index].customerDeal.cpa = deal.customerDeal.commissionType.includes('CPA') && !deal.customerDeal.cpa ? 'Required' : null;
            errors.value[marketName][index].customerDeal.cpl = deal.customerDeal.commissionType.includes('CPL') && !deal.customerDeal.cpl ? 'Required' : null;
            errors.value[marketName][index].customerDeal.rev = deal.customerDeal.commissionType.includes('REV') && !deal.customerDeal.rev ? 'Required' : null;

            // Validate ourDeal
            errors.value[marketName][index].ourDeal.commissionType = deal.ourDeal.commissionType ? null : 'Please select a commission type.';
            errors.value[marketName][index].ourDeal.commissionType = deal.ourDeal.commissionType === 'NOTSET' && deal.voonixAdvertiserId ? 'Please set a commission type.' : null;
            errors.value[marketName][index].ourDeal.cpa = deal.ourDeal.commissionType.includes('CPA') && !deal.ourDeal.cpa ? 'Required' : null;
            errors.value[marketName][index].ourDeal.cpl = deal.ourDeal.commissionType.includes('CPL') && !deal.ourDeal.cpl ? 'Required' : null;
            errors.value[marketName][index].ourDeal.rev = deal.ourDeal.commissionType.includes('REV') && !deal.ourDeal.rev ? 'Required' : null;

            // Always validate voonixAdvertiserId
            if (!deal.voonixAdvertiserId) {
                errors.value[marketName][index].voonixAdvertiserId = 'Please select a Voonix advertiser';
                hasErrors = true;
            } else {
                errors.value[marketName][index].voonixAdvertiserId = null; 
            }
            // Validate selectedUsers
            if (!deal.isPublic) {
                errors.value[marketName][index].selectedUsers =
                    deal.selectedUsers.length === 0 ? 'Please select at least one user' : null;
            } else {
                errors.value[marketName][index].selectedUsers = null;
            }

            // Get all top-level errors except customerDeal and ourDeal
            const topLevelErrors = Object.entries(errors.value[marketName][index])
                .filter(([key]) => key !== 'customerDeal' && key !== 'ourDeal')
                .map(([_, value]) => value);

            if (topLevelErrors.some(error => error !== null) ||
                Object.values(errors.value[marketName][index].customerDeal).some(error => error !== null) ||
                Object.values(errors.value[marketName][index].ourDeal).some(error => error !== null)) {
                hasErrors = true;
            }
        });
    });

    // Return the inverse of hasErrors (true if no errors, false if errors exist)
    return !hasErrors;
};

async function handleAddDeal() {

    if (validateForm()) {
        const modifications = Array.from(modifiedDeals.value.values());
        console.log('Modified Deals:', modifications);
        console.log('Total modified deals:', modifications.length);
        const body = {
            marketName: modifications[0].marketName,
            deal: modifications[0].deal
        }
        const response = await AdvertiserService.addDeal(form.id, body);
        emit('dealAdded', response.data);
        resetForm();
    }
}

function handleSaveOrEdit() {
    if (props.isEditing) {
        editAdvertiser();
    } else {
        saveAdvertiser();
    }
}

//you can't really add any markets yet.
//you need a voonix advertiserFirst, so you need to link it first with our empty space advertiser
async function saveAdvertiser() {
    if (validateForm()) {
        isSaving.value = true;
        console.log('Form is valid. Proceed to save:', form.name);
        const newAdvertiser = {
            name: form.name,
            logoUrl: form.logoUrl,
            markets: advertiserMarkets.value
        };
        try {
            const response = await AdvertiserService.add(newAdvertiser);
            console.log('Advertiser added successfully:', response.data);
            emit('advertiserAdded');
        } catch (error) {
            console.error('Error adding advertiser:', error);
            toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to add advertiser ' + error.response.data.message, life: 3000 });
        } finally {
            isSaving.value = false;
        }
    } else {
        console.log('Form is invalid. Please correct the errors.');
    }
}

watch(() => props.focusDealId, (id) => { if (props.isEditing) focusDeal(id); });

// Replace the watch
watch(
    () => props.advertiserData,
    (newData) => {
        if (props.isEditing && newData) {
            setFormData(newData);
            focusDeal(props.focusDealId);
            marketOptions.value = marketOptions.value.filter((market) => {
                return !newData.markets.includes(market);
            });
        } else {
            resetForm();
        }

        // Handle preset data initialization only once
        if (newData && props.presetData && !hasInitializedPreset.value) {
            const market = advertiserMarkets.value.find(m => m.marketName === props.presetData.marketName);
            if (!market) {
                selectedMarkets.value = [props.presetData.marketName];
                addMarket(props.presetData.siteType, props.presetData.userId);
                hasInitializedPreset.value = true;
            }else{
                addNewDeal(props.presetData.marketName, props.presetData.siteType, props.presetData.userId);
            }
        }
    },
    { immediate: true }
);

// Function to populate form data when editing
function setFormData(advertiser) {
    // Set the name and ID of the advertiser
    form.name = advertiser.name || '';
    form.id = advertiser.id;
    form.logoUrl = advertiser.logoUrl || '';
    // Set the advertiser markets to an array of market objects with `marketName` and `deals`
    advertiserMarkets.value = advertiser.marketDeals || [];

    // Seed the date-effective change inputs for each existing deal. These are
    // transient (underscore-prefixed) and only take effect if the rates change;
    // mapped onto changeType/effectiveFrom/changeReason in editAdvertiser().
    advertiserMarkets.value.forEach(market => {
        (market.deals || []).forEach(deal => {
            deal._changeType = 'change';
            deal._effectiveFrom = new Date();
            deal._changeReason = '';
        });
    });

    // Set the voonix advertisers
    voonixAdvertisers.value = advertiser.voonixAdvertisers || [];
    // Set available markets options
    marketOptions.value = markets;

    // Initialize errors based on each market in `advertiserMarkets`
    errors.value = advertiserMarkets.value.reduce((acc, market) => {
        const marketName = market.marketName;
        const deals = market.deals;

        // Create an array of error objects (one for each deal in the market)
        acc[marketName] = deals.map(() => ({
            name: null,
            dealType: null,
            vertical: null,
            customerDeal: {
                commissionType: null,
                rev: null,
                cpa: null,
                cpl: null
            },
            ourDeal: {
                commissionType: null,
                rev: null,
                cpa: null,
                cpl: null
            },
            voonixAdvertiserId: null
        }));

        return acc;
    }, {});
}

const showAddMarketSection = computed(() => {
    // return !props.isEditing || voonixAdvertisers.value.length === 0;
    return false;
})

// Reset modifications when form is reset
function resetForm() {
    form.name = '';
    form.id = null;
    voonixAdvertisers.value = [];
    hasInitializedPreset.value = false; // Reset the flag when form is reset
    modifiedDeals.value.clear();
}

const fetchUsers = async () => {
    try {
        const response = await UsersService.getAllForDropDown();
        allUsers.value = response.data;
    } catch (error) {
        console.log(error);
    }
};

const fetchLogoData = async () => {
    try {
        const response = await SlugService.getAll();
        logos.value = response.data;
    } catch (error) {
        console.error('Error fetching logo data:', error);
    }
};

const isRefreshingLogos = ref(false);

const refreshLogos = async () => {
    try {
        isRefreshingLogos.value = true;
        await SlugService.import();
        // Refresh the logos list
        await fetchLogoData();
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Logos refreshed successfully',
            life: 3000
        });
    } catch (error) {
        console.error('Error refreshing logos:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to refresh logos',
            life: 3000
        });
    } finally {
        isRefreshingLogos.value = false;
    }
};

onMounted(async () => {
    console.log('onMounted');
    await Promise.all([
        fetchUsers(),
        fetchLogoData()
    ]);
});

// Import the margin calculation function
import { getDealMargin } from '@/utils/deals';

// Add these methods to your component
const getMarginDisplay = (customerDeal, ourDeal) => {
    if (ourDeal.commissionType === 'NOTSET') return '';
    const margin = getDealMargin(customerDeal, ourDeal);

    if (!margin) return '';

    let display = '';

    if (margin.cpa !== undefined) {
        display += `${margin.cpa.toFixed(0)}%`;
    }

    if (margin.rev !== undefined) {
        if (display) display += ' | ';
        display += `REV: ${margin.rev.toFixed(0)}%`;
    }

    if (margin.cpl !== undefined) {
        if (display) display += ' | ';
        display += `CPL: ${margin.cpl.toFixed(1)}%`;
    }

    return display || 'N/A';
};

const getMarginClass = (customerDeal, ourDeal) => {
    const margin = getDealMargin(customerDeal, ourDeal);

    if (!margin) return 'bg-gray-200 text-gray-700';
    if (ourDeal.commissionType === 'NOTSET') return 'bg-gray-200 text-gray-700';

    // Check if any margin component is negative
    const hasNegativeMargin =
        (margin.cpa !== undefined && margin.cpa < 0) ||
        (margin.rev !== undefined && margin.rev < 0) ||
        (margin.cpl !== undefined && margin.cpl < 0);

    // Check if any margin component is below 8%
    const hasLowMargin =
        (margin.cpa !== undefined && margin.cpa < 8 && margin.cpa >= 0) ||
        (margin.rev !== undefined && margin.rev < 8 && margin.rev >= 0) ||
        (margin.cpl !== undefined && margin.cpl < 8 && margin.cpl >= 0);

    // Check if all margin components are 8% or higher
    const allGoodMargin =
        (margin.cpa === undefined || margin.cpa >= 8) &&
        (margin.rev === undefined || margin.rev >= 8) &&
        (margin.cpl === undefined || margin.cpl >= 8);

    if (hasNegativeMargin) {
        return 'bg-red-500 text-red-800'; // Negative margins - red
    } else if (hasLowMargin) {
        return 'bg-red-500 text-white'; // Low margins (< 8%) - yellow
    } else if (allGoodMargin) {
        return 'bg-green-100 text-green-800'; // Good margins (≥ 8%) - green
    } else {
        return 'bg-gray-200 text-gray-700'; // Default/unknown - gray
    }
};

// Add this computed function after the other computed properties
const isDealDisabled = (marketName, index) => {
    if (!props.presetData) return false; // If no preset data, all deals are enabled
    
    // The deal is disabled if it's not the last added deal when we have preset data
    return !(marketName === lastAddedMarket.value && index === lastAddedDealIndex.value);
};

const handleCustomerCommissionTypeChange = (event, marketName, index) => {
    const selectedCommissionType = event.value;
    console.log('Customer commission type changed to:', selectedCommissionType);
    console.log('Market:', marketName, 'Index:', index);
    console.log('Advertiser markets:', advertiserMarkets.value);
    
    // Check if the market exists
    if (!advertiserMarkets.value.find(m => m.marketName === marketName)) {
        console.error('Market not found:', marketName);
        console.log('Advertiser markets:', advertiserMarkets.value);
        return;
    }
    
    // Check if the deal exists
    if (!advertiserMarkets.value.find(m => m.marketName === marketName).deals[index]) {
        console.error('Deal not found at index:', index);
        return;
    }
    
    console.log('Before update - ourDeal.commissionType:', advertiserMarkets.value.find(m => m.marketName === marketName).deals[index].ourDeal.commissionType);
    
    // Automatically set ourDeal commissionType to match customerDeal commissionType
    advertiserMarkets.value.find(m => m.marketName === marketName).deals[index].ourDeal.commissionType = selectedCommissionType;
    
    console.log('After update - ourDeal.commissionType:', advertiserMarkets.value.find(m => m.marketName === marketName).deals[index].ourDeal.commissionType);
};

</script>

<style scoped>
.modified-deal {
    border-color: #22c55e; /* Green color */
    border-width: 2px;
    transition: all 0.3s ease;
}

/* Higher specificity while keeping the original blue color */
.focused-deal {
    outline: 2px solid var(--p-primary-color, #3b82f6);
    outline-offset: 2px;
    transition: outline-color 0.4s ease;
}

.highlight-border {
    border-color: #4f46e5 !important; /* Original indigo color */
    border-width: 2px !important;
    box-shadow: 0 0 10px rgba(79, 70, 229, 0.5) !important;
}
.p-select.p-disabled, .p-multiselect.p-disabled{
background-color: var(--main-background-color) !important;
}

.p-inputtext:disabled{
    background: var(--main-background-color) !important;
    background-color: var(--main-background-color) !important;
}

.p-inputnumber:disabled{
    background: var(--main-background-color) !important;
    background-color: var(--main-background-color) !important;

}

</style>